import { GameMap, Stages, Vec, descend, octileDir } from '../shared/map';
import { stagePlan, StagePlan } from '../shared/sim';

/**
 * What the stages look like (docs/plans/2026-10-17-pacing.md, step 4): the land outside the play area dimmed, police
 * tape along its edge (cars stop short of it: the traffic layer closes its roads), chevrons on the
 * ground towards the zone, and the hint when you push against the line. Pure drawing: the masks come from the same shared code as the sim's.
 */

const TAPE = 'rgba(242,194,48,.85)', CHEVRON = 'rgba(255,181,71,.75)';
export const STAGE_LABEL = ['GATHER', 'MARCH', 'STAND'];

interface Edge { x0: number; y0: number; x1: number; y1: number }

export class StageView {
  private dims = new Map<string, HTMLCanvasElement>();
  private edges = new Map<string, Edge[]>();
  readonly metre: number;
  constructor(readonly m: GameMap, readonly st: Stages) { this.metre = m.tile / (m.mpt || 1); }

  plan(start: number): StagePlan { return stagePlan(this.m, this.st, start); }
  mask(start: number, stage: number): Uint8Array {
    const k = this.plan(start).masks;
    return [k.gather, k.march, k.stand][stage];
  }

  /** One pixel per tile: transparent inside the area, dark outside. Drawn stretched over the world or the minimap. */
  dim(start: number, stage: number): HTMLCanvasElement {
    const key = start + ':' + stage;
    let c = this.dims.get(key);
    if (!c) {
      const k = this.mask(start, stage), m = this.m;
      c = document.createElement('canvas'); c.width = m.w; c.height = m.h;
      const g = c.getContext('2d')!, img = g.createImageData(m.w, m.h), a = Math.round(this.st.bounds.outsideDim * 255);
      for (let i = 0; i < k.length; i++) if (!k[i]) { img.data[i * 4] = 8; img.data[i * 4 + 1] = 10; img.data[i * 4 + 2] = 16; img.data[i * 4 + 3] = a; }
      g.putImageData(img, 0, 0);
      this.dims.set(key, c);
    }
    return c;
  }

  /** The area's border where it meets walkable ground (walls need no line), in world px. */
  private edgeList(start: number, stage: number): Edge[] {
    const key = start + ':' + stage;
    let E = this.edges.get(key);
    if (E) return E;
    E = [];
    const k = this.mask(start, stage), m = this.m, t = m.tile;
    for (let i = 0; i < k.length; i++) {
      if (!k[i]) continue;
      const x = i % m.w, y = (i / m.w) | 0;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= m.w || ny >= m.h) continue;
        const j = ny * m.w + nx;
        if (k[j] || m.solid[j]) continue;
        // the shared side of tiles i and j
        const ex = dx > 0 ? x + 1 : dx < 0 ? x : null, ey = dy > 0 ? y + 1 : dy < 0 ? y : null;
        const seg = ex !== null ? { x0: ex * t, y0: y * t, x1: ex * t, y1: (y + 1) * t } : { x0: x * t, y0: ey! * t, x1: (x + 1) * t, y1: ey! * t };
        E.push(seg);
      }
    }
    this.edges.set(key, E);
    return E;
  }

  /** Dim outside the area (only for those it holds) and draw its police lines. World coordinates. */
  drawBounds(g: CanvasRenderingContext2D, start: number, stage: number, bounded: boolean, vx0: number, vy0: number, vw: number, vh: number) {
    if (bounded) {
      const s = g.imageSmoothingEnabled; g.imageSmoothingEnabled = false;
      g.drawImage(this.dim(start, stage), 0, 0, this.m.width, this.m.height);
      g.imageSmoothingEnabled = s;
    }
    const u = this.metre, pad = this.m.tile;
    // police tape: thin, a little slack, across roads as well
    g.lineCap = 'butt'; g.setLineDash([1.6 * u, 0.4 * u]); g.lineWidth = 0.22 * u; g.strokeStyle = TAPE;
    g.beginPath();
    for (const e of this.edgeList(start, stage)) {
      if (Math.max(e.x0, e.x1) < vx0 - pad || Math.min(e.x0, e.x1) > vx0 + vw + pad || Math.max(e.y0, e.y1) < vy0 - pad || Math.min(e.y0, e.y1) > vy0 + vh + pad) continue;
      g.moveTo(e.x0, e.y0); g.lineTo(e.x1, e.y1);
    }
    g.stroke(); g.setLineDash([]);
  }

  /** Metres to the zone along the march, from (x, y) world px; null when there is no way. */
  distanceM(start: number, x: number, y: number): number | null {
    const F = this.plan(start).march, m = this.m, tx = Math.floor(x / m.tile), ty = Math.floor(y / m.tile);
    const v = tx < 0 || ty < 0 || tx >= m.w || ty >= m.h ? -1 : F[ty * m.w + tx];
    return v < 0 ? null : v * (m.mpt || 1);
  }

  /** The way to the zone from (x, y): a unit vector, or null. */
  way(start: number, x: number, y: number): Vec | null {
    const d = octileDir(this.m, this.plan(start).march, x, y);
    return d && (d.x || d.y) ? d : null;
  }

  /** Unit vector from (x, y) to the route `n` tiles further on: where the march is heading, not just the next step. */
  ahead(start: number, x: number, y: number, n: number): Vec | null {
    const m = this.m, F = this.plan(start).march, tx = Math.floor(x / m.tile), ty = Math.floor(y / m.tile);
    if (tx < 0 || ty < 0 || tx >= m.w || ty >= m.h || F[ty * m.w + tx] <= 0) return null;
    const path = descend(m, F, ty * m.w + tx), i = path[Math.min(n, path.length - 1)];
    const dx = ((i % m.w) + 0.5) * m.tile - x, dy = (((i / m.w) | 0) + 0.5) * m.tile - y, d = Math.hypot(dx, dy);
    return d > 0 ? { x: dx / d, y: dy / d } : null;
  }

  /** Chevrons on the ground along the next `aheadM` metres of the way to the zone. World coordinates. */
  drawChevrons(g: CanvasRenderingContext2D, start: number, x: number, y: number, aheadM: number, time: number) {
    const m = this.m, F = this.plan(start).march, tx = Math.floor(x / m.tile), ty = Math.floor(y / m.tile);
    if (tx < 0 || ty < 0 || tx >= m.w || ty >= m.h || F[ty * m.w + tx] <= 0) return;
    const path = descend(m, F, ty * m.w + tx), u = this.metre, n = Math.ceil(aheadM / (m.mpt || 1));
    const c = (i: number) => ({ x: ((i % m.w) + 0.5) * m.tile, y: (((i / m.w) | 0) + 0.5) * m.tile });
    g.lineWidth = 0.35 * u; g.lineCap = 'round'; g.lineJoin = 'round';
    for (let k = 1; k < Math.min(path.length, n + 1); k++) {
      const a = c(path[k - 1]), b = c(path[k]), ang = Math.atan2(b.y - a.y, b.x - a.x);
      // two chevrons per tile, fading with distance, drifting forward so they read as "this way"
      for (const f of [0.25, 0.75]) {
        const s = (f + (time * 0.6) % 0.5), px = a.x + (b.x - a.x) * s, py = a.y + (b.y - a.y) * s;
        g.globalAlpha = Math.max(0.15, 1 - (k - 1 + s) / (n + 1));
        g.save(); g.translate(px, py); g.rotate(ang);
        g.beginPath(); g.moveTo(-0.6 * u, -0.9 * u); g.lineTo(0.4 * u, 0); g.lineTo(-0.6 * u, 0.9 * u);
        g.strokeStyle = CHEVRON; g.stroke(); g.restore();
      }
    }
    g.globalAlpha = 1;
  }

  /** Where the zone is, for the minimap marker: the centre of its tiles, in world px. */
  zoneCentre(): Vec {
    const m = this.m; let sx = 0, sy = 0, n = 0;
    for (let y = 0; y < m.h; y++) for (let x = 0; x < m.w; x++) if (m.rows[y][x] === 'P') { sx += x + 0.5; sy += y + 0.5; n++; }
    return { x: (sx / (n || 1)) * m.tile, y: (sy / (n || 1)) * m.tile };
  }
}
